import { useState, useEffect } from 'react';
import { message } from '@/components';
import { useLocalStore } from 'mobx-react';
import { DEVICE_STATUS_REQUEST } from '@/api';
import { history, useIntl } from 'umi';
import { meeting as meetingModel, user as userModel, rtc, speakWeight } from '@/models';
import { join } from '@/utils/NERTC/mainClient';

interface Params {
  meetingId: string;
}

let timer: any = 0;
let uids: any = [];
export default function useRtcJoinChannel(params: Params) {
  const { meetingId } = params;
  const { formatMessage } = useIntl();
  const {
    user,
    meeting,
    rtc: RTCInstance,
  } = useLocalStore(() => {
    return {
      user: userModel,
      meeting: meetingModel,
      rtc,
    };
  });

  const peerOnlineCallback = async (yxuid) => {
    console.log('peer-online,入会通知', yxuid);
    uids.push(yxuid);
    clearTimeout(timer);
    timer = setTimeout(async () => {
      await meeting.queryMemberInfoByYXUid(RTCInstance.cid, uids);
      uids = [];
    }, 10);
  };

  const joinChannel = async () => {
    await join({
      channelName: meeting.info?.channelName || '',
      yxuid: user.yxuid || '',
      token: meeting.info?.token || '',
      peerOnline: peerOnlineCallback,
      peerLeave: (yxuid) => {
        meeting.removeMember(Number(yxuid));
        speakWeight.clearRemoteUserWeight(String(yxuid));
        console.log('4444会议的远程说话人userWieght', speakWeight.userWeightMap.get(String(yxuid)));
        if (String(yxuid) === speakWeight?.previousSpeakYuxid) {
          speakWeight.setPreviosusSpeakYuxid('');
        }
      },
      muteAudio: (yxuid) => {
        RTCInstance.muteAudio(Number(yxuid));
      },
      unMuteAudio: (yxuid) => {
        RTCInstance.unmuteAudio(Number(yxuid));
      },
      networkQuality: (data) => {
        data.forEach((item) => {
          const value = RTCInstance.networkQuality.get(item.uid.toString());
          const quality = !(item?.downlinkNetworkQuality >= 4 || item?.uplinkNetworkQuality >= 4);
          if (value === undefined || value !== quality) {
            RTCInstance.networkQuality.set(item.uid.toString(), quality);
          }
          RTCInstance.networkQualityMap.set(item.uid.toString(), {
            downlinkNetworkQuality: item.downlinkNetworkQuality,
            uplinkNetworkQuality: item.uplinkNetworkQuality,
          });
        });
        const badNetworkQuality = data.find(
          (n) => n.downlinkNetworkQuality >= 3 || n.uplinkNetworkQuality >= 3
        );
        if (
          badNetworkQuality &&
          badNetworkQuality.uid === meeting.info?.yxAuth?.yxMeetingUid?.toString()
        ) {
          badNetworkQuality.isSelf = true;
        }
      },
      addStream: async (evt) => {
        const { stream, mediaType } = evt;
        const yxuid = stream.getId();
        // 如果视频流不存在则设置默认状态
        if (!RTCInstance.remoteStreamsMap.get(yxuid)) {
          stream.setSubscribeConfig({
            audio: true,
            video: false,
            screen: true,
          });
        }
        // 音频自动播放
        if (mediaType === 'audio') {
          stream.setSubscribeConfig({
            audio: true,
          });
          // 必须要订阅才可以推送播放，云信sdk需要这样做
          await RTCInstance.client.subscribe(stream);
          // // 在同声传译中 如果加入一个新的流进来只能被监听不能播放,否则这个人声音就能在主频道被听见
          // if (!simultaneousInterpretation.mainChannelMute) {
          await stream.play();
          // }
          setTimeout(async () => {
            const status = await stream.isPlaying('audio');
            RTCInstance.setAudioIsPlaying(status);
          }, 200);
        }
        // 屏幕共享自动订阅
        if (mediaType === 'screen') {
          stream.setSubscribeConfig({
            screen: true,
          });
          await RTCInstance.client.subscribe(stream);
        }
        // await this.client.subscribe(stream);
        yxuid && RTCInstance.updateRemoteStreams(stream, yxuid, mediaType);
      },
      removeStream: (stream) => {
        const yxUid = stream.getId();
        if (!stream.audio && !stream.video && !stream.screen) {
          RTCInstance.removeRemoteStreams(yxUid);
        } else {
          RTCInstance.updateRemoteStreams(stream, yxUid);
        }
      },
      activeSpeaker: (data) => {
        let yxuid = data.uid;
        const voiceLevel = RTCInstance.localStream && RTCInstance.localStream.getAudioLevel();
        if (voiceLevel > data.level) {
          // 当前说话的人
          yxuid = user.yxuid;
        }
        // 当前说话的人
        meeting && meeting.setSpeakerYxUid(yxuid);
      },
      volumeIndicator: (data) => {
        // console.log('=====远程音量', data);
        data?.map((item: { uid: string; level: number }) => {
          speakWeight.setRemoteUserWeight(item.uid, item.level);
        });
      },
      clientClosed: () => {
        user?.logout(meetingId).catch((e) => console.error(e));
        // 会议已结束
        history.push({
          pathname: `feedback`,
        });
      },
      clientBanned: (yxuid) => {
        console.warn(`===== ${yxuid}被提出房间`, yxuid);
        if (String(yxuid) === String(user.yxuid)) {
          message.info({
            content: formatMessage({ id: 'KICK_OUT' }),
            noIcon: true,
          });
          setTimeout(() => {
            user?.logout(meetingId).catch((e) => console.error(e));
            history.push({
              pathname: `feedback`,
            });
          });
        }
      },
      audioTrackEnded: () => {
        message.info({
          content: formatMessage({ id: 'audioCaptureFailed' }),
          noIcon: true,
        });
      },
      videoTrackEnded: () => {
        message.info({
          content: formatMessage({ id: 'videoCaptureFailed' }),
          noIcon: true,
        });
      },
    });
  };

  useEffect(() => {
    const init = async () => {
      if (RTCInstance && RTCInstance.client && user && meeting) {
        await RTCInstance.join(user.yxuid, joinChannel);
        if (meeting.screenUid === user.mid && !RTCInstance.isShareScreen) {
          RTCInstance.setIsShareScreen(false);
        }
        await user.reportDeviceStatus(
          String(RTCInstance.cid),
          RTCInstance.isOpenAudio && !meeting.info?.quiet
            ? DEVICE_STATUS_REQUEST.OPEN
            : DEVICE_STATUS_REQUEST.CLOSE,
          RTCInstance.isOpenVideo ? DEVICE_STATUS_REQUEST.OPEN : DEVICE_STATUS_REQUEST.CLOSE
        );

        // 新入会的用户
        if (!window.sessionStorage.getItem('is_first_join')) {
          window.sessionStorage.setItem('is_first_join', 'true');
          // 主持人设置新入会人员静音
          if (meeting.info?.quiet) {
            message.info({
              content: formatMessage({ id: 'MUTE_ON_ENTRY' }),
              duration: 0,
              close: true,
            });
            await RTCInstance.closeMic();
          }
        }
      }
    };
    init().catch((e) => console.error(e));
  }, [RTCInstance, RTCInstance.client, meeting, user]);
}
